import { useMemo } from "react";
import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import { Link } from "react-router-dom";
import {
  TbBarbell,
  TbPlugConnected,
  TbCircleCheck,
  TbClock,
  TbUsers,
  TbUserPlus,
  TbBrandWhatsapp,
} from "react-icons/tb";
import styles from "./Academias.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

const WHATSAPP_PHONE = "5551992747402";

const ACADEMIAS_FAQ = [
  {
    question: "O bot altera alguma coisa no meu NextFit?",
    answer:
      "Não. A integração é somente leitura: o Nexus consulta os dados e responde seus alunos, mas nunca cria, edita ou apaga nada no NextFit.",
  },
  {
    question: "Preciso já usar o NextFit?",
    answer:
      "Não. O bot atende sua academia normalmente, respondendo dúvidas sobre horários, planos e serviços. Se você usa o NextFit, ele vai além e responde com os dados reais da sua academia (somente leitura).",
  },
  {
    question: "Quanto tempo leva pra configurar?",
    answer: "Conectamos sua conta e o bot começa a responder em pouco tempo.",
  },
];

const ACADEMIAS_MOCKUP = [
  { type: "out", text: "Oi, que horas tem aula de spinning hoje?" },
  { type: "in", text: "Hoje tem spinning às 18h e às 19h30." },
  { type: "out", text: "E meu plano tá em dia?" },
  {
    type: "in",
    text: "Seu plano Mensal está ativo até 15/07. Quer que eu te lembre antes de vencer?",
  },
];

const SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Chatbot de WhatsApp para Academia",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  areaServed: "BR",
  serviceType: "Chatbot de WhatsApp para Academia",
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ACADEMIAS_FAQ.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const BENEFITS = [
  {
    Icon: TbClock,
    title: "Atende enquanto você treina o aluno",
    text: "Perguntas de horário, modalidade e valores respondidas na hora, mesmo longe do balcão.",
  },
  {
    Icon: TbPlugConnected,
    title: "Conversa com o seu NextFit",
    text: "O bot consulta agenda de aulas, planos e contratos no NextFit e responde com dados reais. Somente leitura.",
  },
  {
    Icon: TbUsers,
    title: "Menos fila na recepção",
    text: "Dúvidas repetitivas saem do balcão e vão pro WhatsApp, liberando sua equipe.",
  },
  {
    Icon: TbUserPlus,
    title: "Captura quem ainda não é aluno",
    text: "Interessados perguntam sobre planos a qualquer hora e viram lead automaticamente.",
  },
];

const NEXTFIT_BULLETS = [
  "Consulta a agenda de aulas e horários",
  "Informa planos e valores",
  "Verifica a situação do contrato do aluno",
];

const RECURSOS = [
  "Responde sobre horários e modalidades",
  "Informa planos e valores",
  "Consulta a situação do contrato",
  "Lembretes e avisos pelo WhatsApp",
  "Funciona 24/7, inclusive fim de semana",
];

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.ACADEMIAS,
  business_segment: BUSINESS_SEGMENTS.ACADEMIAS,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Página de segmento vende AGENDA (o cliente marca hora): o teste que ela
  // oferece é o de Agendamento, e o `?produto=` leva isso ao signup.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Cadastro já carregando o produto — o backend escolhe o plano de trial. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const Academias = () => {
  const waHref = useMemo(() => {
    const page =
      typeof window !== "undefined"
        ? window.location.href
        : "https://nexustech.tec.br/academias";
    const text = `Olá! Gostaria de falar com a equipe da Nexus Tech via site. \n\nPágina:${page}`;
    return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;
  }, []);

  return (
    <>
      <SeoHead
        title="Chatbot WhatsApp para Academia que Atende 24/7 | Nexus Chatbot"
        description="Seu aluno tira dúvidas de horários, aulas, planos e contratos pelo WhatsApp a qualquer hora. Integra com o NextFit para responder com os dados reais da sua academia. Teste grátis 14 dias."
        canonical="https://nexustech.tec.br/academias"
        jsonLd={[SERVICE_LD, FAQ_LD]}
      />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCol}>
            <span className={styles.eyebrow}>Para academias</span>
            <h1 className={styles.heroTitle}>
              <TbBarbell
                className={styles.titleIcon}
                size={36}
                aria-hidden="true"
              />
              O atendimento da sua academia no WhatsApp, 24 horas por dia
            </h1>
            <p className={styles.heroText}>
              O bot responde dúvidas sobre horários, aulas, planos e contratos
              puxando as informações direto do seu NextFit — sem você parar o
              treino pra responder mensagem.
            </p>
            <div className={styles.heroCtas}>
              <Link
                to={SIGNUP_PATH}
                className={styles.ctaPrimary}
                onClick={() =>
                  trackTrialCtaClick({
                    ...CTA_CTX,
                    cta_placement: CTA_PLACEMENTS.HERO,
                  })
                }
              >
                Testar grátis por 14 dias
              </Link>
              <a
                href={waHref}
                className={styles.ctaWhats}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TbBrandWhatsapp size={20} aria-hidden="true" />
                Falar no WhatsApp
              </a>
            </div>
            <p className={styles.heroNote}>
              Sem cartão. Cancele quando quiser.
            </p>
          </div>
          <div className={styles.heroMockup}>
            <WhatsAppMockup messages={ACADEMIAS_MOCKUP} looping />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>
            Por que academias escolhem a Nexus
          </h2>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((item, i) => (
              <div key={i} className={styles.benefitCard}>
                <item.Icon
                  className={styles.benefitIcon}
                  size={36}
                  aria-hidden="true"
                />
                <h3 className={styles.benefitTitle}>{item.title}</h3>
                <p className={styles.benefitText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA intermediário */}
      <section className={styles.ctaMid}>
        <div className={styles.ctaMidInner}>
          <p className={styles.ctaMidText}>
            Configuração rápida. Teste por 14 dias sem pagar nada.
          </p>
          <Link
            to={SIGNUP_PATH}
            className={styles.ctaPrimary}
            onClick={() =>
              trackTrialCtaClick({
                ...CTA_CTX,
                cta_placement: CTA_PLACEMENTS.MIDDLE,
              })
            }
          >
            Quero testar agora
          </Link>
        </div>
      </section>

      {/* Integração com o NextFit */}
      <section className={styles.section}>
        <div className={styles.nextfitInner}>
          <TbPlugConnected
            className={styles.nextfitIcon}
            size={40}
            aria-hidden="true"
          />
          <h2 className={styles.sectionTitle}>Integração com o NextFit</h2>
          <p className={styles.nextfitText}>
            Já usa o NextFit pra gerir a academia? O Nexus se conecta a ele e
            responde seus alunos com informações reais — horários de aula,
            modalidades, planos e situação do contrato. A integração é somente
            leitura: o bot consulta e responde, nunca cria, edita ou apaga nada
            no seu NextFit.
          </p>
          <ul className={styles.nextfitList}>
            {NEXTFIT_BULLETS.map((b, i) => (
              <li key={i} className={styles.nextfitItem}>
                <TbCircleCheck
                  className={styles.nextfitItemIcon}
                  size={20}
                  aria-hidden="true"
                />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Recursos */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionTitle}>
            Recursos pensados pra academia
          </h2>

          <div className={styles.recursosGrid}>
            {RECURSOS.map((r, i) => (
              <div key={i} className={styles.recursoCard}>
                <p className={styles.recursoText}>
                  <TbCircleCheck
                    className={styles.recursoIcon}
                    size={18}
                    aria-hidden="true"
                  />
                  {r}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.faq}>
        <div className={styles.faqInner}>
          <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
          <FaqAccordion items={ACADEMIAS_FAQ} />
        </div>
      </section>

      {/* CTA final */}
      <section className={styles.ctaFinal}>
        <div className={styles.ctaFinalInner}>
          <h2 className={styles.ctaFinalTitle}>
            Pronto pra tirar o atendimento do balcão?
          </h2>
          <div className={styles.ctaFinalCtas}>
            <Link
              to={SIGNUP_PATH}
              className={styles.ctaInverse}
              onClick={() =>
                trackTrialCtaClick({
                  ...CTA_CTX,
                  cta_placement: CTA_PLACEMENTS.FINAL,
                })
              }
            >
              Testar grátis por 14 dias
            </Link>
            <a
              href={waHref}
              className={styles.ctaFinalWhats}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TbBrandWhatsapp size={20} aria-hidden="true" />
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Academias;
